import { useState } from 'react';
import { useMutation, useQueryClient, useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useCampaigns } from '@/hooks/useCampaigns';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { AlertCircle, CheckCircle2 } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';

interface CampaignAssignmentProps {
    leadId: string;
    currentCampaignId: string | null;
    onAssigned?: () => void;
}

export function CampaignAssignment({ leadId, currentCampaignId, onAssigned }: CampaignAssignmentProps) {
    const [selectedCampaignId, setSelectedCampaignId] = useState<string>('');
    const queryClient = useQueryClient();
    const { data: campaigns, isLoading: campaignsLoading } = useCampaigns(true);

    // Fetch lead's campaign_id if not provided (e.g., called from Job page)
    const { data: leadData, isSuccess: leadChecked } = useQuery({
        queryKey: ['lead-campaign-check', leadId],
        queryFn: async () => {
            if (currentCampaignId !== null) return null;

            const { data, error } = await supabase
                .from('leads')
                .select('campaign_id')
                .eq('id', leadId)
                .single();

            if (error) throw error;
            return data;
        },
        enabled: currentCampaignId === null,
    });

    // Use provided campaign_id or fetch it from lead
    const actualCampaignId = currentCampaignId ?? leadData?.campaign_id ?? null;

    const { mutate: assignCampaign, isPending } = useMutation({
        mutationFn: async (campaignId: string) => {
            const { error } = await supabase
                .from('leads')
                .update({ campaign_id: campaignId })
                .eq('id', leadId);

            if (error) throw error;
        },
        onSuccess: () => {
            toast.success('Campaign assigned successfully!', {
                description: 'The lead now has a campaign attribution.'
            });
            queryClient.invalidateQueries({ queryKey: ['lead', leadId] });
            queryClient.invalidateQueries({ queryKey: ['lead-campaign-check', leadId] });
            queryClient.invalidateQueries({ queryKey: ['leads'] });
            queryClient.invalidateQueries({ queryKey: ['jobs'] });
            if (onAssigned) onAssigned();
        },
        onError: (error) => {
            toast.error('Failed to assign campaign', {
                description: error.message
            });
        }
    });

    // Don't show if campaign is already assigned, or before the lead lookup has
    // answered — otherwise the warning flashes on every job/lead that does have one.
    if (actualCampaignId || (currentCampaignId === null && !leadChecked)) {
        return null;
    }

    const handleAssign = () => {
        if (!selectedCampaignId) {
            toast.error('Please select a campaign first');
            return;
        }
        assignCampaign(selectedCampaignId);
    };

    return (
        <Card className="border-warning/20 bg-warning/5">
            <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                    <AlertCircle className="h-5 w-5 text-warning" />
                    No Campaign Assigned
                </CardTitle>
                <CardDescription>
                    This lead has no campaign attribution. Assign one now to include it in campaign reports.
                </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
                <Alert>
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription>
                        <span className="font-medium">Note:</span> Once assigned, the campaign cannot be changed through this interface.
                    </AlertDescription>
                </Alert>

                <div className="flex gap-3">
                    <Select
                        value={selectedCampaignId}
                        onValueChange={setSelectedCampaignId}
                        disabled={campaignsLoading || isPending}
                    >
                        <SelectTrigger className="flex-1">
                            <SelectValue placeholder={campaignsLoading ? "Loading campaigns..." : "Select a campaign"} />
                        </SelectTrigger>
                        <SelectContent>
                            {campaigns?.map((campaign) => (
                                <SelectItem key={campaign.id} value={campaign.id}>
                                    {campaign.name}
                                    {campaign.metros?.name && ` (${campaign.metros.name})`}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>

                    <Button
                        onClick={handleAssign}
                        disabled={!selectedCampaignId || isPending}
                        className="gap-2"
                    >
                        <CheckCircle2 className="h-4 w-4" />
                        {isPending ? 'Assigning...' : 'Assign Campaign'}
                    </Button>
                </div>
            </CardContent>
        </Card>
    );
}
